import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EntryTemplateModal = ({ isOpen, onClose, onApplyTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    {
      id: 1,
      name: 'Depreciation Expense',
      description: 'Monthly depreciation for fixed assets',
      icon: 'TrendingDown',
      category: 'Adjusting Entry',
      lineItems: [
        { account: '6100 - Depreciation Expense', debit: 0, credit: 0 },
        { account: '1510 - Accumulated Depreciation', debit: 0, credit: 0 }
      ]
    },
    {
      id: 2,
      name: 'Prepaid Insurance Adjustment',
      description: 'Monthly allocation of prepaid insurance',
      icon: 'Shield',
      category: 'Adjusting Entry',
      lineItems: [
        { account: '5200 - Insurance Expense', debit: 0, credit: 0 },
        { account: '1300 - Prepaid Insurance', debit: 0, credit: 0 }
      ]
    },
    {
      id: 3,
      name: 'Accrued Salaries',
      description: 'Record unpaid salaries at period end',
      icon: 'Users',
      category: 'Accrual',
      lineItems: [
        { account: '5100 - Salaries Expense', debit: 0, credit: 0 },
        { account: '2200 - Salaries Payable', debit: 0, credit: 0 }
      ]
    },
    {
      id: 4,
      name: 'Interest Accrual',
      description: 'Accrue interest expense on loans',
      icon: 'Percent',
      category: 'Accrual',
      lineItems: [
        { account: '5300 - Interest Expense', debit: 0, credit: 0 },
        { account: '2300 - Interest Payable', debit: 0, credit: 0 }
      ]
    },
    {
      id: 5,
      name: 'Revenue Recognition',
      description: 'Recognize earned revenue from unearned',
      icon: 'DollarSign',
      category: 'Revenue',
      lineItems: [
        { account: '2400 - Unearned Revenue', debit: 0, credit: 0 },
        { account: '4100 - Service Revenue', debit: 0, credit: 0 }
      ]
    },
    {
      id: 6,
      name: 'Bad Debt Expense',
      description: 'Write off uncollectible accounts',
      icon: 'AlertTriangle',
      category: 'Adjusting Entry',
      lineItems: [
        { account: '5400 - Bad Debt Expense', debit: 0, credit: 0 },
        { account: '1110 - Allowance for Doubtful Accounts', debit: 0, credit: 0 }
      ]
    }
  ];

  const handleApplyTemplate = () => {
    if (selectedTemplate) {
      onApplyTemplate(selectedTemplate);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Entry Templates</h2>
            <p className="text-sm text-muted-foreground mt-1">Select a template to quickly create common journal entries</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates?.map((template) => (
              <div
                key={template?.id}
                onClick={() => setSelectedTemplate(template)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                  selectedTemplate?.id === template?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedTemplate?.id === template?.id ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    <Icon
                      name={template?.icon}
                      size={24}
                      color={selectedTemplate?.id === template?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-heading font-semibold text-base text-foreground">{template?.name}</h3>
                      {selectedTemplate?.id === template?.id && (
                        <Icon name="CheckCircle" size={20} className="text-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{template?.description}</p>
                    <span className="inline-block mt-2 px-2 py-1 rounded-md text-xs font-medium bg-secondary/10 text-secondary">
                      {template?.category}
                    </span>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-semibold text-foreground">Accounts:</p>
                      {template?.lineItems?.map((line, index) => (
                        <p key={index} className="text-xs text-muted-foreground">
                          • {line?.account}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApplyTemplate} disabled={!selectedTemplate}>
            <Icon name="FileText" size={16} className="mr-2" />
            Apply Template
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EntryTemplateModal;